import React from "react";
import PropTypes from "prop-types";
import { Grid, Header } from "semantic-ui-react";
import LibAdmProfileForm from "./ProfileForm/LibrarianAdminProfileForm";
import StudentProfileForms from "./ProfileForm/StudentProfileForms";
import ProfileAvatar from "./ProfileAvatar";
import { useIsMobile } from "@utils/mobileUtils";

const COLORS = {
  primary: "#225745",
  text: "#333",
  textSecondary: "#666",
};

// Helper to check if user is faculty
const isFacultyUser = (user) => {
  if (!user) {
    return false;
  }

  // Check roles array
  if (user.roles && Array.isArray(user.roles)) {
    const result = user.roles.some((role) => {
      const roleStr = typeof role === "string" ? role : role?.user_type || "";

      return roleStr.toLowerCase() === "faculty";
    });
    if (result) {
      return true;
    }
  }

  // Check userType array
  if (
    user.userType &&
    Array.isArray(user.userType) &&
    user.userType.length > 0
  ) {
    const result = user.userType.some((userType) => {
      const type = userType?.user_type || "";

      return type.toLowerCase() === "faculty";
    });
    return result;
  }

  return false;
};

const UserInfo = ({ user }) => {
  const isMobile = useIsMobile();
  const displayRoles = user?.roles
    ? Array.isArray(user.roles)
      ? user.roles.join(", ")
      : user.roles
    : "";

  return (
    <>
      <Header
        as={isMobile ? "h3" : "h2"}
        style={{
          color: COLORS.text,
          margin: "0 0 0.5rem 0",
          fontSize: isMobile ? "1.3rem" : "1.5rem",
          textAlign: "center",
        }}
      >
        My Profile
      </Header>
      <p
        style={{
          color: COLORS.textSecondary,
          fontSize: isMobile ? "0.85rem" : "0.9rem",
          textAlign: "center",
        }}
      >
        Manage your profile settings and personal information
      </p>

      {user?.full_name && (
        <div style={{ marginTop: "1rem" }}>
          <Header
            as="h4"
            style={{
              color: COLORS.primary,
              margin: "0 0 0.25rem 0",
              textAlign: "center",
              fontSize: isMobile ? "1.1rem" : "1.2rem",
            }}
          >
            Welcome, {user.full_name}
          </Header>
          {user.email && (
            <p
              style={{
                color: COLORS.textSecondary,
                margin: 0,
                fontSize: isMobile ? "0.8rem" : "0.85rem",
                textAlign: "center",
              }}
            >
              {user.email}
              {displayRoles && ` • ${displayRoles}`}
            </p>
          )}
        </div>
      )}
    </>
  );
};

const ProfileTabContent = ({
  user,
  isLibrarianOrAdmin,
  onSubmit,
  onImageUpdate,
  onImageError,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div>
        <div style={{ marginBottom: "2rem" }}>
          <ProfileAvatar
            user={user}
            onImageUpdate={onImageUpdate}
            onError={onImageError}
          />
          <UserInfo user={user} />
        </div>
        <div>
          {isLibrarianOrAdmin || isFacultyUser(user) ? (
            <LibAdmProfileForm
              user={{
                ...user,
                roles: Array.isArray(user.roles)
                  ? user.roles
                  : typeof user.roles === "string"
                  ? user.roles.split(",").map((role) => role.trim())
                  : [],
              }}
              onSubmit={onSubmit}
            />
          ) : (
            <StudentProfileForms user={user} onSubmit={onSubmit} />
          )}
        </div>
      </div>
    );
  }

  return (
    <Grid stackable>
      <Grid.Row>
        <Grid.Column width={6}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <ProfileAvatar
              user={user}
              onImageUpdate={onImageUpdate}
              onError={onImageError}
            />
            <UserInfo user={user} />
          </div>
        </Grid.Column>
        <Grid.Column width={10}>
          {isLibrarianOrAdmin || isFacultyUser(user) ? (
            <LibAdmProfileForm
              user={{
                ...user,
                roles: Array.isArray(user.roles)
                  ? user.roles
                  : typeof user.roles === "string"
                  ? user.roles.split(",").map((role) => role.trim())
                  : [],
              }}
              onSubmit={onSubmit}
            />
          ) : (
            <StudentProfileForms user={user} onSubmit={onSubmit} />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

UserInfo.propTypes = {
  user: PropTypes.shape({
    full_name: PropTypes.string,
    email: PropTypes.string,
    roles: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

ProfileTabContent.propTypes = {
  user: PropTypes.object,
  isLibrarianOrAdmin: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onImageUpdate: PropTypes.func.isRequired,
  onImageError: PropTypes.func,
};

export default ProfileTabContent;
